import { useEffect, useRef } from "react";
import { config, type Favicon } from "../environment";

type StatusState = "waiting" | "building" | "thinking" | "loading" | "resting";
type FaviconState = "default" | "attention" | "done";
type FaviconKind = "workflow" | "chat";

const WORKFLOWS_PATH =
  "M7.38493 11.6925C7.38493 9.99306 6.00694 8.61507 4.30753 8.61507C2.60812 8.61507 1.23014 9.99306 1.23014 11.6925C1.23014 13.3919 2.60812 14.7699 4.30753 14.7699C6.00694 14.7699 7.38493 13.3919 7.38493 11.6925ZM8.61507 11.6925C8.61507 13.3919 9.99306 14.7699 11.6925 14.7699C13.3387 14.7699 14.6832 13.4768 14.7657 11.8511L14.7699 11.6925L14.7657 11.5338C14.6832 9.90812 13.3387 8.61507 11.6925 8.61507C9.31329 8.61507 7.38493 6.68671 7.38493 4.30753C7.38493 2.60812 6.00694 1.23014 4.30753 1.23014C2.60812 1.23014 1.23014 2.60812 1.23014 4.30753C1.23014 6.00694 2.60812 7.38493 4.30753 7.38493C6.68671 7.38493 8.61507 9.31329 8.61507 11.6925ZM8.61507 4.30753C8.61507 5.95374 9.90812 7.29825 11.5338 7.38081L11.6925 7.38493L11.8511 7.38081C13.4768 7.29825 14.7699 5.95374 14.7699 4.30753C14.7699 2.60812 13.3919 1.23014 11.6925 1.23014C9.99306 1.23014 8.61507 2.60812 8.61507 4.30753ZM16 11.6925C16 14.0716 14.0716 16 11.6925 16H4.30753C1.92836 16 0 14.0716 0 11.6925V4.30753C0 1.92836 1.92836 0 4.30753 0H11.6925C14.0716 0 16 1.92836 16 4.30753V11.6925Z";

const ASK_PATH =
  "M8 5C8 3.34315 9.34315 2 11 2C12.6569 2 14 3.34315 14 5C14 6.65685 12.6569 8 11 8C12.6569 8 14 9.34315 14 11C14 12.6569 12.6569 14 11 14C9.34315 14 8 12.6569 8 11C8 12.6569 6.65685 14 5 14C3.34315 14 2 12.6569 2 11C2 9.34315 3.34315 8 5 8C3.34315 8 2 6.65685 2 5C2 3.34315 3.34315 2 5 2C6.65685 2 8 3.34315 8 5Z";

const ACCENT_CIRCLE = `<circle cx="12.9226" cy="12.9226" r="3.0774"`;

const STATE_VARS: Record<Exclude<FaviconState, "default">, string> = {
  attention: "--orange-600",
  done: "--green-600",
};

function resolveCssVar(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

const ENV_FILLS: Record<Favicon, string> = {
  "logo-red.svg": "#FA7476",
  "logo-yellow.svg": "#F7B249",
  "logo-purple.svg": "#C08BEE",
};

function getBaseFill(): string {
  if (config.favicon) return ENV_FILLS[config.favicon];
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "white"
    : "#181818";
}

let currentFaviconState: FaviconState = "default";
let currentFaviconKind: FaviconKind = "workflow";

function buildMark(baseFill: string): string {
  if (currentFaviconKind === "chat") {
    // The ask icon has built-in padding; scale it up so it fills the favicon.
    return `<g transform="translate(8 8) scale(1.25) translate(-8 -8)"><path d="${ASK_PATH}" stroke="${baseFill}" stroke-width="0.8" stroke-linejoin="round"/></g>`;
  }
  return `<path d="${WORKFLOWS_PATH}" fill="${baseFill}"/>`;
}

function buildFaviconHref(state: FaviconState): string {
  const baseFill = getBaseFill();
  const accentFill =
    state === "default" ? null : resolveCssVar(STATE_VARS[state]);
  const accent = accentFill ? `${ACCENT_CIRCLE} fill="${accentFill}"/>` : "";
  // Cut a gap around the indicator so it isn't flush with the icon beneath it.
  const cut = !!accentFill;
  const defs = cut
    ? `<defs><mask id="indicator-cut"><rect width="16" height="16" fill="white"/><circle cx="12.9226" cy="12.9226" r="5.08" fill="black"/></mask></defs>`
    : "";
  const mark = cut
    ? `<g mask="url(#indicator-cut)">${buildMark(baseFill)}</g>`
    : buildMark(baseFill);
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">` +
      defs +
      mark +
      accent +
      `</svg>`
  )}`;
}

function applyFavicon() {
  const href = buildFaviconHref(currentFaviconState);
  const links = document.querySelectorAll('link[rel="icon"]');
  links.forEach((link) => {
    (link as HTMLLinkElement).href = href;
  });
}

function setFavicon(state: FaviconState) {
  currentFaviconState = state;
  applyFavicon();
}

export function useFavicon(
  statusState: StatusState,
  isTypewriterAnimating: boolean,
  kind: FaviconKind = "workflow"
): void {
  const prevState = useRef<StatusState | null>(null);
  const wasAnimating = useRef(false);
  const hadActivity = useRef(false);
  const savedHrefs = useRef<string[]>([]);

  // Save original favicons on mount, restore on unmount.
  // Listen for color scheme changes to re-apply the current state.
  useEffect(() => {
    const links = document.querySelectorAll('link[rel="icon"]');
    savedHrefs.current = Array.from(links).map(
      (link) => (link as HTMLLinkElement).href
    );

    currentFaviconKind = kind;
    setFavicon("default");

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", applyFavicon);

    return () => {
      mq.removeEventListener("change", applyFavicon);
      const links = document.querySelectorAll('link[rel="icon"]');
      links.forEach((link, i) => {
        const el = link as HTMLLinkElement;
        const saved = savedHrefs.current[i];
        if (saved) el.href = saved;
      });
    };
  }, [kind]);

  useEffect(() => {
    const prev = prevState.current;
    prevState.current = statusState;

    if (prev === null) return;

    if (statusState === "waiting") {
      setFavicon("attention");
      return;
    }

    if (statusState !== "resting") {
      hadActivity.current = true;
      setFavicon("default");
      return;
    }

    // Resting — show done if there was activity and typewriter is already finished
    if (hadActivity.current && !isTypewriterAnimating) {
      hadActivity.current = false;
      setFavicon("done");
    }
  }, [statusState, isTypewriterAnimating]);

  useEffect(() => {
    const prev = wasAnimating.current;
    wasAnimating.current = isTypewriterAnimating;

    if (!prev) return;

    if (
      !isTypewriterAnimating &&
      statusState === "resting" &&
      hadActivity.current
    ) {
      hadActivity.current = false;
      setFavicon("done");
    }
  }, [isTypewriterAnimating, statusState]);
}
